import { useEffect, useState } from "react";

interface Answer {
  id: string;
  answerText: string;
  questionId: string;
  question: {
    id: string;
    questionText: string;
  };
}

interface KnowledgeReference {
  query: string;
  references: Array<{
    answerId: string;
    relevanceScore: number;
  }>;
  timestamp: number;
}

interface KnowledgeReferencesProps {
  references: KnowledgeReference[];
}

interface AnswerAccordionItemProps {
  answerId: string;
  index: number;
  accordionName: string;
  isFirst: boolean;
}

function AnswerAccordionItem({
  answerId,
  index,
  accordionName,
  isFirst,
}: AnswerAccordionItemProps) {
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchAnswer() {
      try {
        const response = await fetch(`/api/v1/answers/${answerId}`);
        if (!response.ok) {
          throw new Error("Failed to fetch answer");
        }
        const data = await response.json();
        setAnswer(data);
      } catch (err) {
        console.error("Error fetching answer:", err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchAnswer();
  }, [answerId]);

  if (isLoading) {
    return (
      <div className="collapse collapse-arrow join-item border-base-300 border">
        <input type="radio" name={accordionName} disabled />
        <div className="collapse-title font-medium">
          <span className="loading loading-spinner loading-sm mr-2"></span>
          Loading...
        </div>
      </div>
    );
  }

  if (!answer) {
    return null;
  }

  return (
    <div className="collapse collapse-arrow join-item border-base-300 border">
      <input type="radio" name={accordionName} defaultChecked={isFirst} />
      <div className="collapse-title font-medium text-base">
        {answer.question.questionText}
      </div>
      <div className="collapse-content">
        <p className="text-sm leading-relaxed whitespace-pre-wrap">
          {answer.answerText}
        </p>
      </div>
    </div>
  );
}

export default function KnowledgeReferences({
  references,
}: KnowledgeReferencesProps) {
  if (references.length === 0) {
    return (
      <div className="text-center text-base-content/60 py-8">
        <p className="text-sm">No knowledge references yet.</p>
        <p className="text-xs mt-2">
          Ask questions to see relevant information here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {references.map((ref, refIndex) => (
        <div key={ref.timestamp} className="space-y-3">
          <div className="join join-vertical w-full">
            {ref.references.map((reference, index) => (
              <AnswerAccordionItem
                key={reference.answerId}
                answerId={reference.answerId}
                index={index}
                accordionName={`accordion-${ref.timestamp}`}
                isFirst={index === 0}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
